if (!customElements.get('vortex-cascade-picker')) {
  customElements.define(
    'vortex-cascade-picker',
    class VortexCascadePicker extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.productUrl = this.dataset.url;
        this.formId = this.dataset.formId;
        this.productMake = this.dataset.make || '';

        // {variantId: "Make | Model | Year\n..."} — raw fitment_table text per variant
        const fitmentRaw = JSON.parse(
          this.querySelector('[data-vortex-fitment]').textContent
        );
        // {variantId: {available, featured_media_id}}
        this.variantMeta = JSON.parse(
          this.querySelector('[data-vortex-meta]').textContent
        );

        // Parse into structured rows: {variantId: [{make, model, year}, ...]}
        this.variantRows = {};
        Object.entries(fitmentRaw).forEach(([id, text]) => {
          const rows = this._parseFitment(text);
          if (rows.length) this.variantRows[id] = rows;
        });

        this.modelSelect = this.querySelector('[data-cascade-model]');
        this.yearSelect = this.querySelector('[data-cascade-year]');
        this.fitsBlock = this.querySelector('[data-fits-block]');

        this._populateModels();
        this.modelSelect.addEventListener('change', () => this._onModelChange());
        this.yearSelect.addEventListener('change', () => this._onYearChange());
        this._restoreFromPreselected();
      }

      _restoreFromPreselected() {
        const form = document.getElementById(this.formId);
        const input = form?.querySelector('input[name="id"]');
        const variantId = input?.value || new URLSearchParams(location.search).get('variant');
        if (!variantId) return;

        const rows = this.variantRows[variantId];
        if (!rows || !rows.length) return;
        const { make, model, year } = rows[0];

        this.modelSelect.value = model;
        this._onModelChange();
        this.yearSelect.value = year;

        const meta = this.variantMeta[variantId] || {};
        this._showFits({ make: make || this.productMake, model, year, available: meta.available !== false });
      }

      // ── Parsing ─────────────────────────────────────────────────────────────

      // "Make | Model | Year\nSuzuki | LT-R450 | 2006\n..." → [{make, model, year}, ...]
      _parseFitment(text) {
        if (!text || !text.trim()) return [];
        const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
        // Skip header row when first column is "make" (case-insensitive)
        const firstCol = lines[0] ? lines[0].split('|')[0].trim().toLowerCase() : '';
        const startIdx = firstCol === 'make' ? 1 : 0;
        return lines.slice(startIdx).map((line) => {
          const c = line.split('|').map((s) => s.trim());
          return { make: c[0] || '', model: c[1] || '', year: c[2] || '' };
        }).filter((r) => r.model && r.year);
      }

      // ── Populate ─────────────────────────────────────────────────────────────

      _populateModels() {
        const allRows = Object.values(this.variantRows).flat();
        const models = [...new Set(allRows.map((r) => r.model))]
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));

        models.forEach((model) => {
          const opt = document.createElement('option');
          opt.value = model;
          opt.textContent = model;
          this.modelSelect.appendChild(opt);
        });
      }

      _onModelChange() {
        const model = this.modelSelect.value;
        this.yearSelect.innerHTML =
          '<option value="" disabled selected>Select Year</option>';
        this.yearSelect.disabled = true;
        this._hideFits();
        if (!model) return;

        // Build year → first matching variantId map
        const yearVariantMap = {};
        Object.entries(this.variantRows).forEach(([id, rows]) => {
          rows.forEach((r) => {
            if (r.model === model && r.year && !yearVariantMap[r.year]) {
              yearVariantMap[r.year] = id;
            }
          });
        });

        // Newest year first
        Object.keys(yearVariantMap)
          .sort((a, b) => Number(b) - Number(a))
          .forEach((year) => {
            const meta = this.variantMeta[yearVariantMap[year]] || {};
            const opt = document.createElement('option');
            opt.value = year;
            opt.textContent = meta.available !== false ? year : `${year} — Sold Out`;
            this.yearSelect.appendChild(opt);
          });

        this.yearSelect.disabled = false;
      }

      _onYearChange() {
        const model = this.modelSelect.value;
        const year = this.yearSelect.value;
        if (!model || !year) return;

        // Find the variant and the exact matching row (for Make)
        let variantId = null;
        let matchedRow = null;
        for (const [id, rows] of Object.entries(this.variantRows)) {
          const row = rows.find((r) => r.model === model && r.year === year);
          if (row) { variantId = id; matchedRow = row; break; }
        }
        if (!variantId) return;

        const meta = this.variantMeta[variantId] || {};
        const available = meta.available !== false;
        const make = matchedRow.make || this.productMake;

        this._setVariantId(variantId, available);
        this._fetchAndUpdateSection(variantId, meta);
        this._showFits({ make, model, year, available });
      }

      // ── Variant resolution ───────────────────────────────────────────────────

      _setVariantId(variantId, available) {
        const form = document.getElementById(this.formId);
        if (!form) return;
        const input = form.querySelector('input[name="id"]');
        if (!input) return;

        input.value = variantId;
        // [GCDBP] Re-enable input kept disabled by no_preselect until first selection
        input.disabled = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));

        const addBtn = form.querySelector('[name="add"]');
        if (!addBtn) return;
        if (available) {
          addBtn.removeAttribute('disabled');
        } else {
          addBtn.setAttribute('disabled', 'disabled');
        }
      }

      // ── Section re-render (price, ATC, product info) ─────────────────────────

      _fetchAndUpdateSection(variantId, meta) {
        // Media gallery — immediate, no fetch needed
        if (meta.featured_media_id) {
          const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
          gallery?.setActiveMedia?.(meta.featured_media_id, true);
        }

        // URL
        window.history.replaceState(
          {},
          '',
          `${this.productUrl}?variant=${variantId}`
        );

        if (this._abortCtrl) this._abortCtrl.abort();
        this._abortCtrl = new AbortController();

        fetch(
          `${this.productUrl}?section_id=${this.sectionId}&variant=${variantId}`,
          { signal: this._abortCtrl.signal }
        )
          .then((r) => r.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            this._applyPrice(html);
            this._applyProductActions(html, meta);
            this._applyProductInfo(html);
            this._applyStockBadge(html);
          })
          .catch((e) => {
            if (e.name !== 'AbortError') console.error('[VortexCascade]', e);
          });
      }

      _applyPrice(html) {
        const id = `price-${this.sectionId}`;
        const src = html.getElementById(id);
        const dst = document.getElementById(id);
        if (src && dst) {
          dst.innerHTML = src.innerHTML;
          dst.classList.remove('visibility-hidden');
        }
      }

      _applyProductActions(html, meta) {
        const formSrc = html.getElementById(this.formId);
        const formDst = document.getElementById(this.formId);
        if (!formSrc || !formDst) return;

        const cartSrc = formSrc.querySelector('.product-actions-add-to-cart');
        const cartDst = formDst.querySelector('.product-actions-add-to-cart');
        if (cartSrc && cartDst) cartDst.className = cartSrc.className;

        const addSrc = formSrc.querySelector('[name="add"]');
        const addDst = formDst.querySelector('[name="add"]');
        if (addSrc && addDst) {
          addDst.innerHTML = addSrc.innerHTML;
          if (meta.available !== false) {
            addDst.removeAttribute('disabled');
          } else {
            addDst.setAttribute('disabled', 'disabled');
          }
        }
      }

      _applyProductInfo(html) {
        const detailsCls = `#ProductPage-${this.sectionId} .product-info-details-list`;
        const srcs = html.querySelectorAll(detailsCls);
        document.querySelectorAll(detailsCls).forEach((dst, i) => {
          if (srcs[i]) dst.innerHTML = srcs[i].innerHTML;
        });

        const metaCls = `.product-info-meta-${this.sectionId}`;
        document.querySelectorAll(metaCls).forEach((dst) => {
          const src = html.querySelector(
            `${metaCls}[data-block-id="${dst.dataset.blockId}"]`
          );
          if (src) {
            dst.classList.remove('visibility-hidden');
            dst.innerHTML = src.innerHTML;
          }
        });
      }

      _applyStockBadge(html) {
        const id = `ProductAvailability-${this.sectionId}`;
        const src = html.getElementById(id);
        const dst = document.getElementById(id);
        if (src && dst) dst.innerHTML = src.innerHTML;
      }

      // ── Fits badge ───────────────────────────────────────────────────────────

      _showFits({ make, model, year, available }) {
        if (!this.fitsBlock) return;
        const label = this.fitsBlock.querySelector('[data-fits-label]');
        if (label) label.textContent = [make, model, year].filter(Boolean).join(' ');
        const soldOut = this.fitsBlock.querySelector('[data-fits-soldout]');
        if (soldOut) soldOut.hidden = available;
        this.fitsBlock.hidden = false;
      }

      _hideFits() {
        if (this.fitsBlock) this.fitsBlock.hidden = true;
      }
    }
  );
}
